/**
 * Discovery — "what should I make?"
 *
 * The shuffle rule is the subtle one. A new order EVERY TIME the screen opens,
 * and a STABLE order while she browses it. Those two pull against each other,
 * and the resolution is a seed minted once on mount and sent with every request:
 * the server orders by a hash of (seed, id), so the sequence is fixed for this
 * visit and different on the next. Re-sorting on every fetch would show her the
 * same recipe twice and hide another entirely.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Icon from '../Icon'
import { Empty, RecipeCard } from './Bits'
import {
  CATEGORY_KINDS, discover, updateRecipe,
  type Category, type DiscoveryResponse, type RecipeRow, type Status,
} from '../../lib/savenmake'

export default function Discover({ categories, onOpen, onCook, libraryVersion }: {
  categories: Category[]
  onOpen: (r: RecipeRow) => void
  onCook: (r: RecipeRow) => void
  /** Anything that changes when the library does — App passes its recipe list. */
  libraryVersion?: unknown
}) {
  /* Minted once, on mount. This is the whole shuffle mechanism. */
  const seed = useRef(Math.random().toString(36).slice(2)).current

  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [status, setStatus] = useState<Status | ''>('')
  const [hideCooked, setHideCooked] = useState(true)
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [data, setData] = useState<DiscoveryResponse | null>(null)
  const [surprise, setSurprise] = useState<RecipeRow | null>(null)
  const [error, setError] = useState<string | null>(null)

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('seed', seed)
    if (search.trim()) p.set('search', search.trim())
    for (const id of picked) p.append('category', id)
    if (status) p.set('status', status)
    if (favoritesOnly) p.set('favorites', '1')
    p.set('hideCooked', hideCooked ? '1' : '0')
    return p
  }, [seed, search, picked, status, favoritesOnly, hideCooked])

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await discover(params))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the collection.')
    }
  }, [params])

  /*
   * Also when the library changes. A new account's samples are seeded AFTER
   * this first loads, and without this it said "your library is empty" over a
   * library holding four recipes until the page was refreshed.
   */
  useEffect(() => { void load() }, [load, libraryVersion])

  const byKind = useMemo(() => {
    const map = new Map<string, Category[]>()
    for (const c of categories.filter((c) => c.count > 0)) {
      map.set(c.kind, [...(map.get(c.kind) ?? []), c])
    }
    return map
  }, [categories])

  const toggle = (id: string) =>
    setPicked((held) => (held.includes(id) ? held.filter((x) => x !== id) : [...held, id]))

  /* "Surprise me" picks from what is ON SCREEN, so an active filter is honoured
     — random bakery, random deli — rather than ignored. */
  const pickOne = () => {
    const pool = data?.recipes ?? []
    if (!pool.length) return
    setSurprise(pool[Math.floor(Math.random() * pool.length)]!)
  }

  const favorite = async (r: RecipeRow) => {
    await updateRecipe(r.id, { favorite: !r.favorite })
    await load()
    setSurprise((s) => (s?.id === r.id ? { ...s, favorite: !r.favorite } : s))
  }

  const activeFilters = picked.length + (status ? 1 : 0) + (search.trim() ? 1 : 0) + (favoritesOnly ? 1 : 0)

  return (
    <>
      <div className="filters">
        <div className="filter-row">
          <input
            className="grow" type="search" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search titles, creators, categories…" aria-label="Search recipes"
          />
          <button type="button" className="accent" onClick={pickOne} disabled={!data?.recipes.length}>
            <Icon name="sparkle" size={16} /> Surprise me
          </button>
        </div>

        {[...byKind.entries()].map(([kind, list]) => (
          <div key={kind} className="filter-row">
            <span className="small muted" style={{ flex: '0 0 96px', fontWeight: 700 }}>
              {CATEGORY_KINDS.find((k) => k.key === kind)?.label ?? kind}
            </span>
            <div className="chips">
              {list.map((c) => (
                <button
                  key={c.id} type="button"
                  className={`chip is-kind ${picked.includes(c.id) ? '' : 'ghost'}`}
                  aria-pressed={picked.includes(c.id)}
                  style={picked.includes(c.id) ? { borderColor: 'var(--brand)', color: 'var(--brand)', borderWidth: 2 } : undefined}
                  onClick={() => toggle(c.id)}
                >
                  {c.name} <span className="muted">{c.count}</span>
                </button>
              ))}
            </div>
          </div>
        ))}

        <div className="filter-row">
          <label className="toggle">
            <input type="checkbox" checked={hideCooked} onChange={(e) => setHideCooked(e.target.checked)} />
            Hide recipes I&rsquo;ve cooked
            {hideCooked && !!data?.excluded.cooked && <span className="muted small">({data.excluded.cooked} hidden)</span>}
          </label>
          <label className="toggle">
            <input type="checkbox" checked={favoritesOnly} onChange={(e) => setFavoritesOnly(e.target.checked)} />
            Favourites only
          </label>
          <select value={status} onChange={(e) => setStatus(e.target.value as Status | '')} style={{ maxWidth: 170 }} aria-label="Status">
            <option value="">Any status</option>
            <option value="want_to_try">Want to try</option>
            <option value="tested">Tested</option>
            <option value="cafe_approved">Cafe approved</option>
          </select>
          {activeFilters > 0 && (
            <button type="button" className="ghost" onClick={() => { setPicked([]); setStatus(''); setSearch(''); setFavoritesOnly(false) }}>
              Clear {activeFilters} filter{activeFilters > 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {surprise && (
        <div className="outcome ok">
          <h3><Icon name="sparkle" size={18} /> How about this one?</h3>
          <div className="deck" style={{ gridTemplateColumns: 'minmax(0, 320px)' }}>
            <RecipeCard recipe={surprise} onOpen={onOpen} onFavorite={favorite} />
          </div>
          <div className="filter-row" style={{ marginTop: 12 }}>
            <button type="button" className="primary" style={{ width: 'auto' }} onClick={() => onCook(surprise)}>
              <Icon name="flame" size={16} /> Begin this recipe
            </button>
            <button type="button" onClick={pickOne}><Icon name="shuffle" size={16} /> Something else</button>
            <button type="button" className="ghost" onClick={() => setSurprise(null)}>Back to browsing</button>
          </div>
        </div>
      )}

      {data && !data.recipes.length && (
        /* An empty screen that explains ITSELF: which rule emptied it, and the
           one control that would bring something back. */
        <Empty icon="search" title="Nothing matches right now">
          {data.total === 0 ? (
            <p>Your library is empty. Import something from a link, a screenshot or by hand and it will show up here.</p>
          ) : hideCooked && data.excluded.cooked > 0 && data.excluded.byFilters === data.total - data.excluded.cooked ? (
            <p>
              All {data.excluded.cooked} matching {data.excluded.cooked === 1 ? 'recipe has' : 'recipes have'} been cooked
              already, and cooked ones are hidden by default.
            </p>
          ) : (
            <p>{data.excluded.byFilters} of your {data.total} recipes were filtered out.</p>
          )}
          <div className="filter-row" style={{ justifyContent: 'center' }}>
            {hideCooked && data.excluded.cooked > 0 && (
              <button type="button" className="primary" style={{ width: 'auto' }} onClick={() => setHideCooked(false)}>
                Show cooked recipes too
              </button>
            )}
            {activeFilters > 0 && (
              <button type="button" onClick={() => { setPicked([]); setStatus(''); setSearch(''); setFavoritesOnly(false) }}>
                Clear the filters
              </button>
            )}
          </div>
        </Empty>
      )}

      {!!data?.recipes.length && !surprise && (
        <>
          <p className="muted small">
            {data.recipes.length} of {data.total} · shuffled for this visit
          </p>
          <div className="deck">
            {data.recipes.map((r) => (
              <RecipeCard key={r.id} recipe={r} onOpen={onOpen} onFavorite={favorite} />
            ))}
          </div>
        </>
      )}
    </>
  )
}
